import { CheckIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline'
import { useCallback, useMemo, useState } from 'react'
import { useQuery, UseQueryArgs } from 'urql'
import { ReadUserSearchQuery } from '../../../gql/graphql'
import { classNames } from '../../_helpers'
import { cacheDatabaseName } from '../../_lib/graphql/exchanges/cacheExchange'
import { readUserSearchQuery } from '../../_lib/graphql/queries'
import { Dialog } from '../../_ui/Dialog'
import { useMyself } from '../UserContext'

type PropTypes = {
  open: boolean
  onClose: () => void
}

const USER_ID_STORAGE_KEY = 'debug-user-id'
type SearchUser = NonNullable<NonNullable<ReadUserSearchQuery['readUserSearch']>[number]>

export const SwitchUserDialog = ({ open, onClose }: PropTypes) => {
  const { canAccess } = useMyself() || {}
  const [searchTerm, setSearchTerm] = useState('')
  const normalizedSearchTerm = searchTerm.trim()
  const [usersResult] = useQuery({
    query: readUserSearchQuery,
    variables: {
      input: {
        query: normalizedSearchTerm
      }
    },
    pause: !open || normalizedSearchTerm.length === 0
  } as UseQueryArgs)

  const selectedUserID = window.localStorage.getItem(USER_ID_STORAGE_KEY)

  const users = useMemo(() => {
    return (usersResult.data?.readUserSearch || []).filter(Boolean) as SearchUser[]
  }, [usersResult.data?.readUserSearch])

  const handleSwitchUser = useCallback((userID: string) => {
    window.localStorage.setItem(USER_ID_STORAGE_KEY, userID)
    window.indexedDB.deleteDatabase(cacheDatabaseName)
    window.location.reload()
  }, [])

  if (!canAccess || !canAccess('ADMIN')) {
    return null
  }

  return (
    <Dialog title={'Benutzer wechseln'} open={open} onClose={onClose} className="sm:max-w-2xl">
      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Benutzer suchen"
            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-900 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
          {normalizedSearchTerm.length === 0 && (
            <div className="p-4 text-sm text-gray-500">Bitte einen Suchbegriff eingeben.</div>
          )}

          {normalizedSearchTerm.length > 0 && usersResult.fetching && (
            <div className="p-4 text-sm text-gray-500">Lade Benutzer...</div>
          )}

          {normalizedSearchTerm.length > 0 && !usersResult.fetching && users.length === 0 && (
            <div className="p-4 text-sm text-gray-500">Keine Benutzer gefunden.</div>
          )}

          {normalizedSearchTerm.length > 0 && !usersResult.fetching && users.map(user => {
            const fullName = `${user.firstName || ''} ${user.surname || ''}`.trim() || 'Unbekannter Benutzer'
            const isSelected = user.id === selectedUserID

            return (
              <button
                key={user.id}
                type="button"
                onClick={() => handleSwitchUser(user.id)}
                className={classNames(
                  'flex w-full items-center justify-between border-b border-gray-200 px-4 py-3 text-left last:border-b-0 hover:bg-white focus:bg-white focus:outline-none',
                  isSelected && 'bg-white'
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    {(user.firstName || user.surname) ? (
                      <span className="text-xs font-semibold">
                        {(user.firstName?.slice(0, 1) || '')}{(user.surname?.slice(0, 1) || '')}
                      </span>
                    ) : (
                      <UserIcon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{fullName}</div>
                  </div>
                </div>
                <div className="ml-4 flex shrink-0 items-center">
                  {isSelected && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700">
                      <CheckIcon className="h-4 w-4" />
                      Aktiv
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </Dialog>
  )
}

export default SwitchUserDialog
